import { CommonErrorCode, LoginErrorCodes, SiteSwitchErrorCodes, UserErrorCode } from '~commonInfra/utils/ErrorCodes';

import { AuthenticationTypeResolutionService } from '../../authentication/services/AuthenticationTypeResolutionService';
import { Messages } from '../../codegen/Localize';
import { ServerSettingsUnauthenticated } from '../../settings/services/ServerSettingsUnauthenticated';
import { AuthenticationType, DomainFamily } from '../../settings/types/IServerSettingsUnauthenticated';

export namespace SignInErrorMessageMapper {

    type ErrorCode = CommonErrorCode | UserErrorCode | SiteSwitchErrorCodes | LoginErrorCodes;
    const codeToErrorMessageMap = new Map<ErrorCode, () => string>()
        .set(CommonErrorCode.SiteUserNotFound, Messages.signInFailedSiteUserNotFound)
        .set(CommonErrorCode.SiteNotFound, Messages.signInFailedSiteNotFound)
        .set(CommonErrorCode.SystemUserNotFound, Messages.signInFailedSystemUserNotFound)
        .set(CommonErrorCode.InvalidSession, Messages.sessionExpired)
        .set(CommonErrorCode.UnlicensedServer, Messages.signInFailedUnlicensedServer)
        .set(UserErrorCode.UserOpenIdUserNotFound, Messages.errorUserNotFound)
        .set(UserErrorCode.UserOpenIdConflictingUserAccount, Messages.conflictingUserAccount)
        .set(UserErrorCode.UserOpenIdIdpNotReachable, Messages.cannotReachOpenIdIdp)
        .set(UserErrorCode.UserOpenIdBadConfig, Messages.openIdBadConfig)
        .set(UserErrorCode.UserInvalidPasswordResetToken, Messages.invalidPasswordResetLink)
        .set(UserErrorCode.UserPasswordResetTokenExpired, Messages.linkExpired)
        .set(UserErrorCode.UserPasswordNotRespectingPolicies, Messages.errorUserPasswordNotRespectingPolicies)
        .set(UserErrorCode.LoginAttemptRejected, Messages.errorAccountIsInLockoutPeriod)
        .set(UserErrorCode.AccountIsBlocked, Messages.errorAccountIsBlocked)
        .set(SiteSwitchErrorCodes.UserUnlicensedForSite, Messages.signInFailedUserUnlicensedForSite)
        .set(SiteSwitchErrorCodes.SiteLocked, Messages.signInFailedSiteLocked)
        .set(SiteSwitchErrorCodes.SiteSuspended, Messages.signInFailedSiteSuspended)
        .set(LoginErrorCodes.ClientCertificateNotFound, Messages.signinFailedClientCertificateNotFound)
        .set(LoginErrorCodes.ClientCertificateUserNotFoundInCertificate, Messages.signinFailedClientCertificateUserNotFoundInCertificate)
        .set(LoginErrorCodes.ClientCertificateUserNotFoundOnServer, Messages.signinFailedClientCertificateUserNotFoundOnServer)
        .set(LoginErrorCodes.KerberosFailed, Messages.signInFailedAutoSignin)
        .set(LoginErrorCodes.SspiFailed, Messages.signInFailedAutoSignin)
        .set(LoginErrorCodes.BadCredentials, Messages.signInFailedBadCredentials)
        .set(LoginErrorCodes.OpenIdFailed, Messages.signInFailedUnknownReason);

    export function getErrorMessageFor(errorCode: number): string {
        const message = codeToErrorMessageMap.get(errorCode);
        if (message) {
            return message();
        } else {
            return Messages.signInFailedUnknownReason();
        }
    }

    export function getFollowUpActionMessageFor(errorCode: number): string | null {
        // Keeping this legacy strategy, it works, but it is terrible design.
        const isOnline = ServerSettingsUnauthenticated.domainFamily() === DomainFamily.External;
        const contactAdmin = isOnline ? Messages.contactSiteAdmin() : Messages.contactServerAdmin();

        switch (errorCode) {
            case CommonErrorCode.SiteNotFound:
            case CommonErrorCode.SystemUserNotFound:
            case CommonErrorCode.UnlicensedServer:
            case UserErrorCode.UserOpenIdUserNotFound:
            case UserErrorCode.UserOpenIdConflictingUserAccount:
            case UserErrorCode.UserOpenIdIdpNotReachable:
            case UserErrorCode.UserOpenIdBadConfig:
            case UserErrorCode.AccountIsBlocked:
            case SiteSwitchErrorCodes.UserUnlicensedForSite:
            case SiteSwitchErrorCodes.SiteLocked:
            case LoginErrorCodes.OpenIdFailed:
                return contactAdmin;

            case LoginErrorCodes.ClientCertificateNotFound:
            case LoginErrorCodes.ClientCertificateUserNotFoundInCertificate:
            case LoginErrorCodes.ClientCertificateUserNotFoundOnServer:
                const isClientCertWithFallback = AuthenticationType.ClientCertificateWithFallback ===
                    AuthenticationTypeResolutionService.authenticationType();
                return isClientCertWithFallback ? Messages.signInUsingTableauServerCredentialsOption() : contactAdmin;

            default:
                return null;
        }
    }

    export function getKbArticleIdFor(errorCode: number): string | null {
        // Keeping this legacy strategy, it works, but it is terrible design.
        const isOnline = ServerSettingsUnauthenticated.domainFamily() === DomainFamily.External;

        if (errorCode === CommonErrorCode.SiteUserNotFound && isOnline) {
            return 'error-the-user-account-is-not-active-when-logging-into-tableau-online-with-an-active-site-admin-account';
        }

        return null;
    }
}

export type SignInErrorMessageMapper = typeof SignInErrorMessageMapper;
